/* eslint-disable react/jsx-props-no-spreading */
import { ComponentChild, FunctionalComponent, h } from 'preact';
import { ListContainer, ListWrapper } from '../List';
import { Ref, useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { TCFVendor, isTcfUI } from '@usercentrics/cmp-browser-sdk';
import { UIService, areServicesFull, useCategoryContext } from '../../contexts/CategoryContext';
import { forwardRef, memo } from 'preact/compat';
import { getIndexesRatio, getItemStyle, getLengthWithExtra } from '../VirtualList/helpers';
import { sortByPropAsc, sortCategories } from '../../modules/sort';
import ControllerIdItem from '../ControllerIdItem';
import MapServices from '../ServiceHandler/MapServices';
import { ScrollToId } from '../../App.helpers';
import { VirtualChildContainer } from '../VirtualList/VirtualList.style';
import VirtualList from '../VirtualList';
import VirtualServiceItem from '../VirtualList/VirtualServiceItem';
import VirtualVendorItem from '../VirtualList/VirtualVendorItem';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useSubService } from '../../hooks/useSubService';
import { useTcfContext } from '../../contexts/TcfContext';
import { useTheme } from 'styled-components';
import { useUiContext } from '../../contexts/UiContext';
import { useVirtualList } from '../../hooks';

const EXTRA_VENDOR_ITEMS = 1;
const EXTRA_SERVICE_ITEMS = 1;
const CONTROLLER_ID_ITEM = 1;

type Props = {
  controllerIdLabel: string;
  isVisible: boolean;
  serviceTitle: string;
  stickyRef: Ref<HTMLDivElement>;
  vendorTitle: string;
};

const VendorsList: FunctionalComponent<Props> = forwardRef<HTMLDivElement, Props>(
  ({ controllerIdLabel, isVisible, serviceTitle, vendorTitle, stickyRef }, virtualListRef) => {
    const { onCloseSubServiceDetail, onOpenSubServiceDetail } = useSubService();
    const { tcfData, setTcfData } = useTcfContext();
    const theme = useTheme();

    const { categories, setCategories, onServiceToggle } = MapServices(useCategoryContext());

    const vendors = useMemo(() => {
      if (tcfData?.vendors) {
        return sortByPropAsc(tcfData.vendors, 'name');
      }

      return [];
    }, [tcfData]);
    const services = useMemo(() => sortCategories(categories), [categories]);
    const { clearServiceVendorScrollToId, scrollToIdTabViewServiceVendors } = useUiContext();

    // Clears the scroll to index to avoid jumping back on it after the component rendered
    useEffect(() => {
      if (isVisible) {
        onCloseSubServiceDetail();
        clearServiceVendorScrollToId();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    /**
     * If has at least one vendor or non-iab vendor selected, then toggle it off
     * Otherwise, flip it.
     */
    const hasOneVendorToggledOn = useMemo(
      () =>
        vendors.some(
          (vendor) =>
            vendor.consent ||
            vendor.legitimateInterestConsent ||
            vendor.consent === null ||
            vendor.legitimateInterestConsent === null,
        ),
      [vendors],
    );
    const hasAllVendorToggledOff = useMemo(
      () => vendors.every((vendor) => !vendor.consent && !vendor.legitimateInterestConsent),
      [vendors],
    );
    const hasOneCategoryToggledOn = useMemo(
      () => services.filter((service) => !service.isEssential).some((service) => service.consent.status),
      [services],
    );
    const hasAllCategoriesToggledOff = useMemo(
      () => services.filter((service) => !service.isEssential).every((service) => !service.consent.status),
      [services],
    );

    const [vendorsLength] = getLengthWithExtra(EXTRA_VENDOR_ITEMS, vendors);
    const [servicesLength] = getLengthWithExtra(EXTRA_SERVICE_ITEMS, services);
    const itemCount = vendorsLength + servicesLength + CONTROLLER_ID_ITEM;
    const { getItemSize, setItemSize } = useVirtualList(itemCount, 50);
    const { ui } = useSettingsContext();
    const [vendorToggleAll, setVendorToggleAll] = useState(false);
    const [categoryToggleAll, setCategoryToggleAll] = useState(false);

    /**
     * IAB VENDORS HANDLERS
     * */
    const setVendorConsents = useCallback(
      (id: number, consent: boolean | null, legitimateInterestConsent: boolean | null) => {
        const index = vendors.findIndex((vendor) => vendor.id === id);

        if (index !== -1 && tcfData) {
          setTcfData({
            ...tcfData,
            vendors: [
              ...vendors.slice(0, index),
              {
                ...vendors[index],
                consent: consent === null ? vendors[index].consent : consent,
                legitimateInterestConsent:
                  legitimateInterestConsent === null
                    ? vendors[index].legitimateInterestConsent
                    : legitimateInterestConsent,
              },
              ...vendors.slice(index + 1),
            ],
          });
        }
      },
      [tcfData, setTcfData, vendors],
    );

    const onToggleVendorConsent = useCallback(
      (id: number, checked: boolean): void => {
        setVendorConsents(id, checked, null);
      },
      [setVendorConsents],
    );

    const onToggleVendorLegInterestConsent = useCallback(
      (id: number, checked: boolean): void => {
        setVendorConsents(id, null, checked);
      },
      [setVendorConsents],
    );

    const resolveToggleAllStatus = useCallback(
      (atLeastOneChecked: boolean, allUnchecked: boolean, toggleAllStatus: boolean): boolean => {
        if (atLeastOneChecked) {
          return false;
        }

        if (allUnchecked) {
          return true;
        }

        return !toggleAllStatus;
      },
      [],
    );

    const onToggleAllVendors = useCallback(() => {
      if (!tcfData) {
        return;
      }
      setTcfData({
        ...tcfData,
        vendors: vendors.map((vendor: TCFVendor) => ({
          ...vendor,
          consent: resolveToggleAllStatus(hasOneVendorToggledOn, hasAllVendorToggledOff, vendorToggleAll),
          legitimateInterestConsent: resolveToggleAllStatus(
            hasOneVendorToggledOn,
            hasAllVendorToggledOff,
            vendorToggleAll,
          ),
        })),
      });
      setVendorToggleAll(resolveToggleAllStatus(hasOneVendorToggledOn, hasAllVendorToggledOff, vendorToggleAll));
    }, [
      setTcfData,
      tcfData,
      vendors,
      resolveToggleAllStatus,
      hasOneVendorToggledOn,
      hasAllVendorToggledOff,
      vendorToggleAll,
    ]);

    const onToggleAllServices = useCallback(() => {
      setCategories(
        categories.map((category) => ({
          ...category,
          services: category.services.map((service: UIService) => ({
            ...service,
            consent: {
              ...service.consent,
              status: resolveToggleAllStatus(hasOneCategoryToggledOn, hasAllCategoriesToggledOff, categoryToggleAll),
            },
          })),
        })),
      );

      setCategoryToggleAll(
        resolveToggleAllStatus(hasOneCategoryToggledOn, hasAllCategoriesToggledOff, categoryToggleAll),
      );
    }, [
      setCategories,
      categories,
      resolveToggleAllStatus,
      hasOneCategoryToggledOn,
      hasAllCategoriesToggledOff,
      categoryToggleAll,
    ]);

    if (!ui || !isTcfUI(ui)) {
      return null;
    }

    const getScrollIndexForId = (): number => {
      let index;

      // If scrollToId is a number or a string representation of a number --> look in vendor list

      if (Number(scrollToIdTabViewServiceVendors)) {
        index = vendors.findIndex((vendorProp) => vendorProp.id === Number(scrollToIdTabViewServiceVendors));

        // index found --> return correct whole list index by adding static Placeholder in the top of the list
        if (index >= 0) return index + EXTRA_VENDOR_ITEMS;
      } else if (scrollToIdTabViewServiceVendors) {
        index = services.findIndex((service: { id: ScrollToId }) => service.id === scrollToIdTabViewServiceVendors);

        // index found --> return correct whole list index by adding static Placeholder in the top of the vendor and service list and the length of the vendor list
        if (index >= 0) {
          if (vendors.length) {
            return index + vendors.length + EXTRA_VENDOR_ITEMS + EXTRA_SERVICE_ITEMS;
          }

          return index + EXTRA_SERVICE_ITEMS;
        }
      }

      return -1;
    };

    const renderItem = (index: number): ComponentChild => {
      const [vendorsIndex, servicesIndex] = getIndexesRatio(vendorsLength, servicesLength);

      if (vendorsLength > 0 && index < vendorsIndex.end) {
        return (
          <VirtualVendorItem
            index={index}
            id={index}
            getItemSize={getItemSize}
            setItemSize={setItemSize}
            title={vendorTitle}
            scrollToId={Number(scrollToIdTabViewServiceVendors)}
            onToggleConsent={onToggleVendorConsent}
            onToggleLegInterestConsent={onToggleVendorLegInterestConsent}
            onToggleAll={onToggleAllVendors}
            vendors={vendors}
          />
        );
      }

      if (
        servicesLength > 0 &&
        index >= servicesIndex.start &&
        index < servicesIndex.end &&
        areServicesFull(services)
      ) {
        return (
          <VirtualServiceItem
            categories={categories}
            getItemSize={getItemSize}
            index={index}
            id={index - servicesIndex.start}
            onOpenSubServiceDetail={onOpenSubServiceDetail}
            onToggleService={onServiceToggle}
            onToggleAllServices={onToggleAllServices}
            setItemSize={setItemSize}
            services={services}
            showToggles={!ui.secondLayer.hideToggles}
            hideToggle={ui.secondLayer.hideToggles}
            title={serviceTitle}
            scrollToId={scrollToIdTabViewServiceVendors}
          />
        );
      }

      // always render controllerID in the last position of the list
      if (index === itemCount - 1) {
        return <ControllerIdItem data-testid="controller-id" label={controllerIdLabel} />;
      }

      return null;
    };

    return (
      <ListContainer>
        <ListWrapper>
          <VirtualList
            data-testid="uc-virtual-list"
            ref={virtualListRef}
            estimatedItemSize={50}
            isVisible={isVisible}
            itemCount={itemCount}
            itemSize={getItemSize}
            stickyRef={stickyRef}
            marginBottom={theme.spacing.base.lg}
            {...(scrollToIdTabViewServiceVendors && { scrollToIndex: getScrollIndexForId() })}
            renderItem={({ index, style }): ComponentChild => (
              <VirtualChildContainer
                data-testid={`uc-virtual-list-item-${index}`}
                key={index}
                style={getItemStyle(style)}
              >
                {renderItem(index)}
              </VirtualChildContainer>
            )}
            width="100%"
          />
        </ListWrapper>
      </ListContainer>
    );
  },
);

export default memo(VendorsList);
